// screens/RadarNewsletter.tsx — R3e (C): "Add to newsletter"'s destination.
// Top = the issue AS IT WILL READ — the server's own `previewMd` rendered
// through MdLite, never a second client-side composition (spec's own rule:
// "one composer function", pythia-eden's composeNewsletter — this screen
// draws only what that function wrote, byte for byte). Below = Manage: the
// ordered item rows (note/move/remove), the issue's own title field, and
// Publish. Past issues at the bottom, each with Read (the kept wiki page's
// markdown, fetched through GET .../issues/:id -> pythia_note_get).
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRadarNewsletter,
  getRadarNewsletterIssue,
  listRadarNewsletterIssues,
  publishRadarNewsletter,
  removeFromRadarNewsletter,
  reorderRadarNewsletter,
  setRadarNewsletterItemNote,
  type RadarNewsletterIssueSummaryApi,
  type RadarNewsletterQueueApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { MdLite } from '../components/MdLite';
import { kindChipLabel } from '../lib/radarKind';
import './spread.css';
import './RadarTopics.css'; // shares .rtopics__label/.rtopics__input with the topics window's own form
import './RadarNewsletter.css';

export interface RadarNewsletterProps {
  tenantId: string;
  onBack: () => void;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready' };
type ReadState = { id: string; title: string; bodyMd: string } | null;

const EMPTY_SAY = 'Nothing queued. Add to newsletter from any item.';

export function RadarNewsletter({ tenantId, onBack }: RadarNewsletterProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [queue, setQueue] = useState<RadarNewsletterQueueApi | null>(null);
  const [issues, setIssues] = useState<RadarNewsletterIssueSummaryApi[]>([]);
  const [titleDraft, setTitleDraft] = useState('');
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [readOpen, setReadOpen] = useState<ReadState>(null);
  const [readBusy, setReadBusy] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  function applyQueue(q: RadarNewsletterQueueApi) {
    setQueue(q);
    // R3g (C3, 2026-09-27) — the field starts blank on every fresh draft (a
    // just-opened queue included): the placeholder, not a stale draft's own
    // title, is what tells Huan a blank field names it by the date.
    setTitleDraft('');
    setNoteDrafts(Object.fromEntries(q.items.map((it) => [it.item.id, it.note ?? ''])));
  }

  function refresh() {
    return Promise.all([getRadarNewsletter(tenantId), listRadarNewsletterIssues(tenantId)])
      .then(([q, iss]) => {
        applyQueue(q);
        setIssues(iss);
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function remove(itemId: string) {
    setBusy(itemId);
    setNote(null);
    try {
      const q = await removeFromRadarNewsletter(tenantId, itemId);
      applyQueue(q);
      setReceipt(q.receipt);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(null);
    }
  }

  async function move(itemId: string, direction: 'up' | 'down') {
    if (!queue) return;
    const order = [...queue.items].sort((a, b) => a.position - b.position).map((it) => it.item.id);
    const i = order.indexOf(itemId);
    const j = direction === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= order.length) return;
    const a = order[i]!;
    const b = order[j]!;
    order[i] = b;
    order[j] = a;
    setBusy(itemId);
    setNote(null);
    try {
      const q = await reorderRadarNewsletter(tenantId, order);
      applyQueue(q);
      setReceipt(q.receipt);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(null);
    }
  }

  // Saves on blur (spec) — but only when the field actually changed and
  // isn't blank (the tool's own note.min(1); an emptied field simply keeps
  // the last saved note rather than being sent as a refusal).
  async function saveNote(itemId: string, committed: string) {
    const value = (noteDrafts[itemId] ?? '').trim();
    if (!value || value === committed.trim()) return;
    setBusy(itemId);
    setNote(null);
    try {
      const q = await setRadarNewsletterItemNote(tenantId, itemId, value);
      applyQueue(q);
      setReceipt(q.receipt);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    setBusy('__publish__');
    setNote(null);
    try {
      const q = await publishRadarNewsletter(tenantId, titleDraft.trim() || undefined);
      applyQueue(q);
      setReceipt(q.receipt);
      setIssues(await listRadarNewsletterIssues(tenantId));
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not published');
    } finally {
      setBusy(null);
    }
  }

  async function readIssue(id: string) {
    if (readOpen?.id === id) {
      setReadOpen(null);
      return;
    }
    setReadBusy(id);
    setReadError(null);
    try {
      const r = await getRadarNewsletterIssue(tenantId, id);
      setReadOpen({ id, title: r.title, bodyMd: r.bodyMd });
    } catch (err) {
      setReadError(err instanceof ApiError ? err.message : 'could not open this issue');
    } finally {
      setReadBusy(null);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error' || !queue) {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">
          The radar is not reachable.
          <br />
          <span class="sp__say-soft">{load.phase === 'error' ? load.message : ''}</span>
        </div>
      </div>
    );
  }

  const orderedItems = [...queue.items].sort((a, b) => a.position - b.position);

  return (
    <div class="sp">
      <BackLink label="Radar" onClick={onBack} />
      <div class="sp__eyebrow">Newsletter</div>
      <h1 class="sp__title">{queue.issue.title}</h1>

      <div class="sp__sec"><span>As it will read</span></div>
      {orderedItems.length === 0 ? (
        <div class="sp__empty">{EMPTY_SAY}</div>
      ) : (
        <div class="rnews__preview">
          <MdLite md={queue.previewMd} />
        </div>
      )}

      {receipt ? <p class="sp__age">{receipt}</p> : null}
      {note ? <p class="sp__age sp__age--late">{note}</p> : null}

      <div class="sp__sec" style={{ marginTop: '20px' }}><span>Manage</span></div>

      {orderedItems.length === 0 ? (
        <div class="sp__empty">{EMPTY_SAY}</div>
      ) : (
        orderedItems.map((it, idx) => (
          <div class="rnews__row" key={it.item.id}>
            <div class="rnews__row-top">
              <span class="sp__chip">{kindChipLabel(it.item.kind, it.item.durationSeconds)}</span>
              <a class="rtopic__item-title" href={it.item.url} target="_blank" rel="noreferrer">
                {it.item.title ?? it.item.url}
              </a>
            </div>
            <div class="rnews__row-sub">{it.item.sourceName}</div>
            <textarea
              class="rnews__note"
              rows={2}
              placeholder={it.readingExcerpt ?? ''}
              value={noteDrafts[it.item.id] ?? ''}
              onInput={(e) => setNoteDrafts((cur) => ({ ...cur, [it.item.id]: (e.target as HTMLTextAreaElement).value }))}
              onBlur={() => void saveNote(it.item.id, it.note ?? '')}
            />
            <div class="rnews__row-acts">
              <button
                type="button"
                class="rnews__act"
                disabled={busy === it.item.id || idx === 0}
                onClick={() => void move(it.item.id, 'up')}
              >
                Move up
              </button>
              <button
                type="button"
                class="rnews__act"
                disabled={busy === it.item.id || idx === orderedItems.length - 1}
                onClick={() => void move(it.item.id, 'down')}
              >
                Move down
              </button>
              <button type="button" class="rnews__act" disabled={busy === it.item.id} onClick={() => void remove(it.item.id)}>
                Remove
              </button>
            </div>
          </div>
        ))
      )}

      {/* R3g (C3, 2026-09-27) — one optional title, right above Publish; a
          blank field keeps the tool's own date title (spec: no instructional
          caption anywhere else, so the placeholder alone carries that). */}
      <label class="rtopics__label" for="rnews-title">Title</label>
      <input
        id="rnews-title"
        class="rtopics__input"
        type="text"
        placeholder="leave blank to name it by the date"
        value={titleDraft}
        onInput={(e) => setTitleDraft((e.target as HTMLInputElement).value)}
      />

      <button
        type="button"
        class="sp__act rnews__publish"
        disabled={busy === '__publish__' || orderedItems.length === 0}
        onClick={() => void publish()}
      >
        Publish to Pythia
      </button>
      <p class="rnews__soft">Publishing keeps this issue in the record as a page and a file. Nobody is sent anything yet.</p>

      <div class="sp__sec" style={{ marginTop: '20px' }}><span>Past issues</span></div>
      {issues.length === 0 ? (
        <div class="sp__empty">Nothing published yet.</div>
      ) : (
        issues.map((i) => (
          <div class="rnews__issue" key={i.id}>
            <div class="rnews__issue-top">
              <span class="rnews__issue-title">{i.title}</span>
              <span class="rnews__issue-sub">
                {i.publishedAt ? new Date(i.publishedAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'date unknown'}
                {' · '}
                {i.itemCount} item{i.itemCount === 1 ? '' : 's'}
              </span>
            </div>
            <button type="button" class="rnews__act" disabled={readBusy === i.id} onClick={() => void readIssue(i.id)}>
              {readOpen?.id === i.id ? 'Close' : 'Read'}
            </button>
            {readOpen?.id === i.id ? (
              <div class="rnews__read">
                <MdLite md={readOpen.bodyMd} />
              </div>
            ) : null}
          </div>
        ))
      )}
      {readError ? <p class="sp__age sp__age--late">{readError}</p> : null}
    </div>
  );
}
